import * as Sentry from "@sentry/vue";
import type { App } from 'vue';
import type { Router } from 'vue-router';
import type { Pinia, PiniaPlugin } from 'pinia';

interface SentryConfig {
    sentryDsn?: string;
    sentryEnabled?: boolean;
    appVersion?: string;
    environment?: string;
    tracesSampleRate?: number;
    sendDefaultPii?: boolean;
    debug?: boolean;
    tags?: Record<string, string | undefined>;
}

interface UseSentryOptions {
    app: App;
    router: Router;
    pinia: Pinia;
    config?: SentryConfig;
}

export function useSentry({ app, router, pinia, config }: UseSentryOptions): void {
    if (!config?.sentryEnabled || !config?.sentryDsn) {
        return;
    }

    pinia.use(Sentry.createSentryPiniaPlugin() as unknown as PiniaPlugin);

    Sentry.init({
        app,
        dsn: config.sentryDsn,
        release: config.appVersion,
        environment: config.environment,
        debug: config.debug ?? false,
        sendDefaultPii: config.sendDefaultPii ?? true,
        enableLogs: true,
        enabled: config.sentryEnabled,
        normalizeDepth: 5,
        tracesSampleRate: config.tracesSampleRate ?? 0.1,
        tunnel: "/sentry",
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: ["localhost", /^\//, /^https:\/\/[^/]*\.crowdworx\.com/],
        integrations: [
            Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: true,
            }),
        ],
        initialScope: {
            tags: transformTags(config.tags)
        }
    });
}

function toKebabCase(str: string): string {
    const kebab = str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    return kebab.charAt(0).toUpperCase() + kebab.slice(1);
}

function transformTags(tags?: Record<string, string | undefined>): Record<string, string> {
    if (!tags) return {};

    return Object.entries(tags).reduce((acc, [key, value]) => {
        if (value !== undefined) {
            acc[toKebabCase(key)] = value;
        }
        return acc;
    }, {} as Record<string, string>);
}
